import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { signupSchema, type SignupFormValues } from "./schemas";
import { authApi } from "@/api/auth-api";
import { useState } from "react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api-error";
import { ProfileClaimedDialog } from "./profile-claimed-dialog";

export function SignupForm({
  invitationCode,
}: {
  invitationCode?: string | null;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [showProfileClaimedDialog, setShowProfileClaimedDialog] =
    useState(false);
  const [claimedCompanyName, setClaimedCompanyName] = useState<string>("");

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const onSubmit = async (values: SignupFormValues) => {
    if (!invitationCode) {
      toast.error(
        getErrorMessage(
          new Error("Invalid invitation link"),
          "Invalid invitation link"
        )
      );
      return;
    }

    setIsLoading(true);
    try {
      const response = await authApi.acceptFounderInvitation(
        invitationCode,
        values.firstName,
        values.lastName,
        values.email
      );

      if (response.status === "success") {
        setClaimedCompanyName(response.result?.company?.name || "");
        setShowProfileClaimedDialog(true);
      } else {
        toast.error(
          getErrorMessage(
            response,
            response.message || "Failed to create account"
          )
        );
      }
    } catch (error: any) {
      toast.error(
        getErrorMessage(error, error.message || "Failed to create account")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    setShowProfileClaimedDialog(false);
    // Founders belong in the startup app (profile → applications), not the
    // investor app — send them there to sign in rather than this app.
    const startupAppUrl =
      import.meta.env.VITE_STARTUPFUEL_APP_URL || "https://app.startupfuel.com";
    window.location.href = startupAppUrl;
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {invitationCode && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              You're accepting an invitation to manage a company. Your account
              will be linked to the company upon signup.
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your first name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="Enter your email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Creating Account..." : "Sign Up"}
        </Button>
      </form>

      <ProfileClaimedDialog
        isOpen={showProfileClaimedDialog}
        onClose={handleDialogClose}
        companyName={claimedCompanyName}
      />
    </Form>
  );
}
